import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Clock,
  Calendar,
  Target,
  User,
  DollarSign,
  FileText,
  CheckCircle,
  AlertTriangle,
  Plus,
  X
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const EditTimeEntry = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { success, error: showError } = useToast();
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    project: '',
    task: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    duration: '',
    billable: true,
    hourlyRate: '',
    totalAmount: '',
    
    // Additional Details
    category: '',
    tags: [],
    notes: '',
    attachments: [],
    
    // Status
    status: 'pending',
    approvedBy: '',
    approvedAt: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);

  // Load time entry data
  useEffect(() => {
    const loadTimeEntry = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        const response = await fetch(`${API_CONFIG.ENDPOINTS.TIME_ENTRIES}/${id}`, {
          headers: API_CONFIG.HEADERS
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to load time entry');
        }

        const data = await response.json();
        const timeEntry = data.data?.timeEntry || data.data || data;
        
        // Format date for input
        const formatDateForInput = (dateString) => {
          if (!dateString) return new Date().toISOString().split('T')[0];
          try {
            return new Date(dateString).toISOString().split('T')[0];
          } catch {
            return dateString;
          }
        };

        setFormData({
          project: timeEntry.project?._id || timeEntry.project || '',
          task: timeEntry.task?._id || timeEntry.task || '',
          description: timeEntry.description || '',
          date: formatDateForInput(timeEntry.date),
          startTime: timeEntry.startTime || '',
          endTime: timeEntry.endTime || '',
          duration: timeEntry.duration?.toString() || '',
          billable: timeEntry.billable !== undefined ? timeEntry.billable : true,
          hourlyRate: timeEntry.hourlyRate?.toString() || '',
          totalAmount: timeEntry.totalAmount?.toString() || '',
          category: timeEntry.category || '',
          tags: timeEntry.tags || [],
          notes: timeEntry.notes || '',
          attachments: timeEntry.attachments || [],
          status: timeEntry.status || 'pending',
          approvedBy: timeEntry.approvedBy?._id || timeEntry.approvedBy || '',
          approvedAt: timeEntry.approvedAt || ''
        });
      } catch (err) {
        console.error('Error loading time entry:', err);
        showError(err.message || 'Failed to load time entry');
        navigate('/projects/time-tracking/entry');
      } finally {
        setLoading(false);
      }
    };

    loadTimeEntry();
  }, [id, navigate, showError]);

  // Load projects and tasks
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}?limit=1000`, {
          headers: API_CONFIG.HEADERS
        });
        if (response.ok) {
          const data = await response.json();
          const projectsList = data.data?.projects || data.projects || data.data || [];
          setProjects(projectsList.map(p => ({
            id: p._id || p.id,
            name: p.name || p.projectName || 'Unnamed Project'
          })));
        }
      } catch (err) {
        console.warn('Failed to load projects:', err);
      }
    };

    loadProjects();
  }, []);

  // Load tasks when project is selected
  useEffect(() => {
    const loadTasks = async () => {
      if (!formData.project) {
        setTasks([]);
        return;
      }

      try {
        const response = await fetch(`${API_CONFIG.ENDPOINTS.TASKS}?project=${formData.project}&limit=1000`, {
          headers: API_CONFIG.HEADERS
        });
        if (response.ok) {
          const data = await response.json();
          const tasksList = data.data?.tasks || data.tasks || data.data || [];
          setTasks(tasksList.map(t => ({
            id: t._id || t.id,
            title: t.title || 'Unnamed Task',
            projectId: t.project?._id || t.project || formData.project
          })));
        }
      } catch (err) {
        console.warn('Failed to load tasks:', err);
      }
    };

    loadTasks();
  }, [formData.project]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-calculate duration and amount
    if (field === 'startTime' || field === 'endTime') {
      calculateDuration();
    }
    if (field === 'duration' || field === 'hourlyRate') {
      calculateAmount();
    }
  };

  // Calculate duration from start and end time
  const calculateDuration = () => {
    if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}`);
      const end = new Date(`2000-01-01T${formData.endTime}`);
      const diffMs = end - start;
      const diffHours = diffMs / (1000 * 60 * 60);
      
      if (diffHours > 0) {
        setFormData(prev => ({
          ...prev,
          duration: diffHours.toFixed(2)
        }));
      }
    }
  };

  // Calculate total amount
  const calculateAmount = () => {
    const duration = parseFloat(formData.duration) || 0;
    const rate = parseFloat(formData.hourlyRate) || 0;
    const amount = duration * rate;
    
    setFormData(prev => ({
      ...prev,
      totalAmount: amount.toFixed(2)
    }));
  };

  // Handle project change
  const handleProjectChange = (projectId) => {
    setFormData(prev => ({
      ...prev,
      project: projectId,
      task: '' // Reset task when project changes
    }));
  };

  // Handle tag addition
  const handleAddTag = (tag) => {
    if (tag.trim() && !formData.tags.includes(tag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag.trim()]
      }));
    }
  };

  // Handle tag removal
  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const timeEntryData = {
        project: formData.project,
        task: formData.task || undefined,
        description: formData.description,
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime || undefined,
        duration: parseFloat(formData.duration) || 0,
        billable: formData.billable,
        hourlyRate: parseFloat(formData.hourlyRate) || 0,
        totalAmount: parseFloat(formData.totalAmount) || 0,
        status: formData.status,
        notes: formData.notes || undefined,
        tags: formData.tags.length > 0 ? formData.tags : undefined
      };

      const response = await fetch(
        `${API_CONFIG.ENDPOINTS.TIME_ENTRIES}/${id}`,
        {
          method: 'PUT',
          headers: {
            ...API_CONFIG.HEADERS,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(timeEntryData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update time entry');
      }

      const result = await response.json();
      if (result.success) {
        success('Time entry updated successfully!');
        navigate('/projects/time-tracking/entry');
      } else {
        throw new Error(result.message || 'Failed to update time entry');
      }
    } catch (err) {
      console.error('Error updating time entry:', err);
      showError(err.message || 'Failed to update time entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get filtered tasks based on selected project
  const getFilteredTasks = () => {
    if (!formData.project) return tasks;
    return tasks.filter(task => task.projectId === formData.project);
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="Edit Time Entry"
          description="Loading time entry..."
        />
        <div className="page-container">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Edit Time Entry"
        description="Update your time entry details"
        actions={[
          {
            label: 'Back',
            icon: <ArrowLeft size={18} />,
            onClick: () => navigate('/projects/time-tracking/entry'),
            variant: 'outline'
          }
        ]}
      />
      <div className="page-container">

      {/* Main Content */}
      <div className="project-create-container">
        <form onSubmit={handleSubmit}>
          {/* Form Card */}
          <div className="project-form-card">
            {/* Basic Information Section */}
            <div className="project-form-section">
              <div className="section-header">
                <h3 className="section-title">Time Entry Details</h3>
                <p className="section-description">Basic information about your time entry</p>
              </div>
              
              {/* 4-Column Grid Layout */}
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Project *</label>
                  <select
                    className="form-select"
                    value={formData.project}
                    onChange={(e) => handleProjectChange(e.target.value)}
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Task</label>
                  <select
                    className="form-select"
                    value={formData.task}
                    onChange={(e) => handleInputChange('task', e.target.value)}
                  >
                    <option value="">Select Task</option>
                    {getFilteredTasks().map(task => (
                      <option key={task.id} value={task.id}>
                        {task.title}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                  >
                    <option value="">Select Category</option>
                    <option value="development">Development</option>
                    <option value="design">Design</option>
                    <option value="testing">Testing</option>
                    <option value="meeting">Meeting</option>
                    <option value="research">Research</option>
                    <option value="documentation">Documentation</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Time Information */}
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Start Time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={formData.startTime}
                    onChange={(e) => handleInputChange('startTime', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">End Time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={formData.endTime}
                    onChange={(e) => handleInputChange('endTime', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Duration (hours) *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                    placeholder="0.00"
                    step="0.25"
                    min="0"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div className="form-group">
                <label className="form-label">Description *</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe what you worked on..."
                  required
                />
              </div>
            </div>

            {/* Billing Information Section */}
            <div className="project-form-section">
              <div className="section-header">
                <h3 className="section-title">Billing Information</h3>
                <p className="section-description">Billing details and rate information</p>
              </div>
              
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Billable</label>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="billable"
                      checked={formData.billable}
                      onChange={(e) => handleInputChange('billable', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="billable">
                      This time entry is billable
                    </label>
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Hourly Rate ($)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.hourlyRate}
                    onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Total Amount ($)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.totalAmount}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Currency</label>
                  <select
                    className="form-select"
                    value="USD"
                    disabled
                  >
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Additional Information Section */}
            <div className="project-form-section">
              <div className="section-header">
                <h3 className="section-title">Additional Information</h3>
                <p className="section-description">Tags, notes, and other details</p>
              </div>
              
              {/* Tags */}
              <div className="form-group">
                <label className="form-label">Tags</label>
                <div className="tags-container">
                  <div className="tags-input">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Add a tag and press Enter"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag(e.target.value);
                          e.target.value = '';
                        }
                      }}
                    />
                  </div>
                  <div className="tags-list">
                    {formData.tags.map((tag, index) => (
                      <span key={index} className="tag">
                        {tag}
                        <button
                          type="button"
                          className="tag-remove"
                          onClick={() => handleRemoveTag(tag)}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Additional notes or comments..."
                />
              </div>
            </div>

            {/* Form Actions */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px', 
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: '1px solid var(--border-color)'
            }}>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate('/projects/time-tracking/entry')}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} className="me-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
      </div>
    </>
  );
};

export default EditTimeEntry;
